/**
 * common radiobutton component used by other mudules as a reusable component which returns ui for radio button i for radio button and required fields can be passed as props
 *
 * @module views/components/RadioButton
 * @memberof -Common
 */
import './RadioButton_Huge.scss';

import React, { useCallback, useRef } from 'react';

import classNames from 'classnames';
import { useField } from 'formik';
import PropTypes from 'prop-types';

import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { processClientActionType } from '@ulta/core/utils/clientActionProcessor/clientActionProcessor';
import { constants } from '@ulta/core/utils/constants/constants';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import * as utils from './RadioButton_Huge';

/**
  * Represents a RadioButton component
  *
  * @method
  * @param {RadioButton_HugeProps} props - React properties passed from composition
  * @returns RadioButton
  */
export const RadioButton_Huge = ( props ) => {
  const {
    children,
    id,
    action,
    alignment,
    crossButtonVisibility,
    tabIndex,
    isChecked,
    isDefaultChecked,
    isDisabled = defaultProps.isDisabled,
    onChange,
    hideInput = defaultProps.hideInput,
    value
  } = props;
  const [field] = useField( props );
  const { openOverlay, isOverlay } = useOverlay();
  const radioRef = useRef();

  const handleChange = useCallback( utils.composeOnChange(
    {
      action,
      field,
      radioRef,
      alignment,
      crossButtonVisibility,
      isChecked
    },
    { openOverlay, isOverlay, onChange }
  ), [onChange] );

  if( !children ){
    return null;
  }

  const sanitizedIndex =  typeof tabIndex === 'number' ? Math.min( 0, tabIndex, Math.max( -1, tabIndex ) ) : null;
  const hasTabIndex = sanitizedIndex !== null;

  return (
    <div className='RadioButton_Huge'>

      <label
        id={ `${id}_label` }
        htmlFor={ id }
        className={ classNames( {
          ...( props.unstyled && {
            'RadioButton_Huge--unstyled': true,
            [props.className]: props.className
          } ),
          ...( !props.unstyled && {
            'RadioButton_Huge__label': true } )
        } ) }

      >
        <input
          ref={ radioRef }
          name={ field.name }
          id={ id }
          type='radio'
          checked={ isChecked }
          defaultChecked={ isDefaultChecked }
          disabled={ isDisabled }
          className={ classNames( 'RadioButton_Huge__input', {
            RadioButton_Huge__hideInput: hideInput
          } ) }
          onChange={ e => handleChange( e ) }
          { ...spreadProps( {
            value,
            tabIndex: hasTabIndex ? sanitizedIndex : undefined,
            'aria-describedby': props.ariaDescribedBy
          } ) }
        />
        { children }
      </label>
    </div>
  );
};

/**
  * Handles onClick for radio buttons
  * @param {object} data arguments
  * @param {object} data.field Formik field helper
  * @param {object} data.action DXL action
  * @param {object} data.radioRef Radio el ref
  * @param {string} data.alignment Flyout alignment
  * @param {boolean} data.crossButtonVisibility Flyout/modal crossButtonVisibility
  * @param {object} methods arguments
  * @param {function} methods.isOverlay isOverlay
  * @param {function} methods.openOverlay openOverlay
  * @param {function} methods.onChange Caller's onChange callback
  */
export const composeOnChange = ( data, methods ) => ( e ) => {
  const { action, field, radioRef = {}, alignment, crossButtonVisibility } = handleEmptyObjects( data );
  const { isOverlay, openOverlay, onChange } = methods || {};
  const { navigationType, dataCaptureData, clientActionType } = action || {};

  if( !field?.onChange || !e?.target ){
    return;
  }

  field.onChange( e );
  onChange && onChange( e );

  if( !e.target.value || e.target.value === 'off' ){
    return;
  }

  if( dataCaptureData ){
    datacapture.processEvents( { dataCapture: { ...dataCaptureData } }, constants.DATACAPTURE_EVENT_TYPE.click );
  }

  if( clientActionType ){
    processClientActionType( { action } );
  }

  if( !isOverlay?.( navigationType ) ){
    return;
  }

  openOverlay( {
    type: action.navigationType,
    opener: radioRef,
    alignment,
    ...( action && { action } ),
    ...( crossButtonVisibility && { crossButtonVisibility } )
  } );
};

/**
  * Property type definitions
  * @typedef RadioButton_HugeProps
  * @type {object}
  * @property {boolean} isChecked - Set the radio button is checked or unchecked
  * @property {boolean} isDefaultChecked -  set the default checked value of a checkbox
  * @property {boolean} isDisabled - the button is un-clickable
  * @property {string} id - Sets id for button which is required field
  * @property {function} onChange - Sets the onChange when this function is called
  * @property {number} tabIndex - Sets the tab focus for element
  * @property {string} value - Sets the value which is required field
  * @property {boolean} hideInput - Set the value for hide the radio or not
  * @property {string} alignment - Set the alignment if there's an overlay
  * @property {boolean} crossButtonVisibility - Set the crossButtonVisibility if there's an overlay
  *
  */

export const propTypes = {
  /** Set the radio button is checked or unchecked */
  isChecked: PropTypes.bool,
  /** Set the default checked value of a checkbox */
  isDefaultChecked: PropTypes.bool,
  /** Set the button is un-clickable */
  isDisabled: PropTypes.bool,
  /** Sets the id for button */
  id: PropTypes.string.isRequired,
  /** Sets the onChange when this function is called */
  onChange: PropTypes.func,
  /** Sets the tab focus for element */
  tabIndex: PropTypes.number,
  /** Sets the value */
  value: PropTypes.string.isRequired,
  /** Sets the value to hide radio or not */
  hideInput: PropTypes.bool,
  /** Sets the value to hide radio or not */
  alignment: PropTypes.string,
  /** Sets the value to hide radio or not */
  crossButtonVisibility: PropTypes.bool,
  /** Sets the value of label */
  children: PropTypes.oneOfType( [PropTypes.element, PropTypes.string] )
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {boolean} isDisabled=false - The default behavoir of the button is un-clickable
  * @property {boolean} hideInput=false - The default behavoir of the radio is displayed
  */
export const defaultProps = {
  isDisabled: false,
  hideInput: false
};

RadioButton_Huge.propTypes = propTypes;
RadioButton_Huge.defaultProps = defaultProps;
RadioButton_Huge.handleOnClick = composeOnChange;

export default RadioButton_Huge;